<template>
  <dialogWrap v-model="show" title="发起支付宝支付" width="810px">
    <div class="alipay-withhold-container" v-if="!inResultPage">
      <div class="alipay-tips">
        <p>温馨提示</p>
        <p>1.支付宝支付单笔限额 {{ ruleConfig.onlineAliLimitSingle }}，单日限额 {{ ruleConfig.onlineAliLimitDay }}，单月限额 {{
          ruleConfig.onlineAliLimitMonth }}，请谨慎操作。</p>
        <p>2.发起代扣前，请确保司机已知晓，否则因此产生的司机投诉，平台将视情况禁用支付宝支付能力。</p>
      </div>
      <div class="alipay-withhold-amount">
        <el-form class="amount-form" ref="amountForm" :model="form" :rules="rules" :validate-on-rule-change="false">
          <el-form-item label="本次代扣金额" prop="amount">
            <div class="amount-input">
              <el-input style="width: 200px" v-number:money v-model="form.amount" @input="amountChange"
                placeholder="请输入代扣金额">
              </el-input>
              <span class="calculate-amount" v-if="showAmountInfo">
                预计实际到账金额: {{ amountInfo.actualReceiptAmount }} 元
                <el-tooltip effect="dark" :content="getTooltipContent" placement="top">
                  <i class="QS-icon-question-outline"></i>
                </el-tooltip>
              </span>
            </div>
            <div class="amount-tips">
              (本次最多可发起支付金额:
              {{
                amountInfo.withholdAmount === null ? 0 : amountInfo.withholdAmount }}元)
            </div>
          </el-form-item>
        </el-form>
      </div>
    </div>

    <div class="alipay-withhold-succ" v-else>
      <el-result icon="success" title="发起成功">
        <template slot="extra">
          <div>稍后可从代扣记录中查看本次代扣结果</div>
          <el-link href="/logCenter/withholdExecLog/list" target="_blank" type="primary">
            >>点此查看代扣记录
          </el-link>
        </template>
      </el-result>
    </div>

    <template slot="footer">
      <div v-if="!inResultPage">
        <el-button @click="show = false">取消</el-button>
        <el-button :loading="btnLoading" type="primary" @click="submit">确认代扣</el-button>
      </div>
      <div v-else>
        <el-button @click="() => {
          emit('executeSuccess')
          show = false
        }">关闭</el-button>
      </div>
    </template>
  </dialogWrap>
</template>
<script setup>
import { actualAlipayWithholdAmount, getAlipayWithholdOverdue, getWithholdConfig, startAlipayWithhold } from '@/api/domain/finance'
import { Message } from '@qs/ui'
import { debounce } from 'lodash'
import { computed, reactive, ref, watchEffect } from 'vue'

const emit = defineEmits(['executeSuccess'])

const show = ref(false)
const inResultPage = ref(false)
const amountForm = ref(null)
const btnLoading = ref(false)

const info = reactive({
  orderId: '',
  receivableIds: [],
})

const form = ref({
  amount: '',
})

const ruleConfig = ref({
  onlineAliChannelRate: '', // 代扣通道手续费
  commissionWithholdAlipay: '', // 代扣技术手续费
  onlineAliLimitSingle: '', // 单笔限额
  onlineAliLimitDay: '', // 单日限额
  onlineAliLimitMonth: '', // 单月限额
  chargeType: '',
})

const amountInfo = reactive({
  overDueAmount: null,
  withholdAmount: null,
  actualReceiptAmount: null,
})

const rules = {
  amount: [
    {
      required: true,
      trigger: 'change',
      validator: (_, value, callback) => {
        if (!value) {
          callback(new Error('请输入代扣金额'))
        } else if (Number(value) === 0) {
          callback(new Error('代扣金额不能为0'))
        } else if (Number(value) > amountInfo.withholdAmount) {
          callback(new Error(`代扣金额不能大于${amountInfo.withholdAmount}元`))
        } else {
          callback()
        }
      },
    },
  ],
}

const showDialog = (orderId, receivableIds) => {
  if (receivableIds) {
    info.receivableIds = receivableIds.split(',')
  }
  info.orderId = orderId
  getWithholdRule()
  getOverdue()
  show.value = true
}

// 计算最终金额
const amountChange = debounce(() => {
  amountInfo.actualReceiptAmount = null
  if (form.value.amount) getActualAmount()
}, 1000)

const getActualAmount = async () => {
  const { code, data } = await actualAlipayWithholdAmount({
    orderId: info.orderId,
    amount: form.value.amount,
  })
  if (code === 1) {
    amountInfo.actualReceiptAmount = data.actualReceiptAmount
  }
}

const getOverdue = async () => {
  const { code, data } = await getAlipayWithholdOverdue({
    orderId: info.orderId,
    receivableIds: info.receivableIds,
  })
  if (code === 1) {
    amountInfo.overDueAmount = data.overDueAmount
    amountInfo.withholdAmount = data.withholdAmount
  }
}

const getWithholdRule = async () => {
  const { code, data } = await getWithholdConfig({
    orderId: info.orderId,
  })
  if (code === 1) {
    ruleConfig.value = data
  }
}

// 确认代扣
const submit = async () => {
  amountForm.value.validate(async (valid) => {
    if (valid) {
      btnLoading.value = true
      const { code } = await startAlipayWithhold({
        rentOrderId: info.orderId,
        receivableIds: info.receivableIds,
        amount: form.value.amount,
      })

      btnLoading.value = false

      if (code === 1) {
        inResultPage.value = true
        Message.success('操作成功')
      }
    }
  })
}

const getOrderDetailPage = computed(() => {
  return `/workbench/rentOrder/detail?id=${info.orderId}&orderType=personal&type=8`
})

const getTooltipContent = computed(() => {
  return `平台会收取每笔代扣金额的 ${ruleConfig.value.commissionWithholdAlipay}% 作为技术服务费。最终请以实际到账金额为准。`
})

const showAmountInfo = computed(() => {
  return form.value.amount !== null && form.value.amount !== '' && amountInfo.actualReceiptAmount !== null
})

watchEffect(() => {
  if (!show.value) {
    inResultPage.value = false
    amountInfo.actualReceiptAmount = null
    form.value.amount = ''
    amountForm.value?.resetFields()
  }
})

defineExpose({
  showDialog,
})
</script>
<style lang="scss" scoped>
.alipay-tips {
  padding: 10px;
  background: #EEF2FC;
  margin-bottom: 20px;
}

.amount-form {

  .amount-input,
  .el-form-item__content {
    display: flex;
  }

  .calculate-amount {
    color: #999999;
    margin-left: 30px;
  }

  .amount-tips {
    color: #999999;
    margin-left: 100px;
  }
}
::v-deep .el-form-item__error {
  margin-left: 100px;
}
</style>
